import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { useI18n } from "@/i18n";
import TurnstileWidget from "@/components/TurnstileWidget";

function NewsletterForm() {
  const { t, lang } = useI18n();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(null);
  const widget = useRef(null);
  const onToken = useCallback((v) => setToken(v), []);
  const onInvalid = useCallback(() => setToken(null), []);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return;
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email, consent, lang, turnstile_token: token });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      widget.current?.reset(); // the token is single-use, success or failure
      setToken(null);
    }
  };

  if (done) {
    return <p className="text-xs text-[#00FF87]" data-testid="newsletter-done">{t("footer.newsletter_done")}</p>;
  }
  return (
    <form onSubmit={submit} className="space-y-2" data-testid="newsletter-form">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t("footer.newsletter_email")}
        data-testid="newsletter-email-input"
        className="w-full rounded-md border border-[#1F293D] bg-transparent px-3 py-2 text-xs text-slate-200 focus:border-[#00FF87] focus:outline-none"
      />
      <label className="flex items-start gap-2 text-[11px] leading-snug text-slate-500">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid="newsletter-consent-checkbox"
          className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[#00FF87]"
        />
        {t("footer.newsletter_consent")}
      </label>
      {consent && <TurnstileWidget ref={widget} action="newsletter" onToken={onToken} onInvalid={onInvalid} />}
      <button
        type="submit"
        disabled={busy || !consent || !token}
        data-testid="newsletter-submit-button"
        className="flex items-center gap-1.5 rounded-md border border-[#1F293D] px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-wider text-slate-300 transition-colors hover:border-[#00FF87]/50 hover:text-[#00FF87] disabled:opacity-40"
      >
        <Mail className="h-3 w-3" /> {t("footer.newsletter_btn")}
      </button>
    </form>
  );
}

export default function Footer() {
  const { t, lang, setLang } = useI18n();
  const cols = [
    {
      title: t("footer.product"),
      links: [
        { to: "/how-it-works", label: t("footer.how") },
        { to: "/pricing", label: t("footer.pricing") },
        { to: "/feedback", label: t("footer.feedback") },
      ],
    },
    {
      title: t("footer.legal"),
      // New tab on every legal page: these links also sit inside the payment funnel
      // (/checkout renders this footer), where following one in place drops the session
      // and the plan the user had selected.
      links: [
        { to: "/legal/mentions-legales", label: t("footer.mentions"), blank: true },
        { to: "/legal/cgu", label: t("footer.cgu"), blank: true },
        { to: "/legal/cgv", label: t("footer.cgv"), blank: true },
        { to: "/legal/confidentialite", label: t("footer.privacy"), blank: true },
        { to: "/legal/cookies", label: t("footer.cookies"), blank: true },
      ],
    },
    {
      title: t("footer.account"),
      links: [
        { to: "/auth", label: t("footer.login_link") },
        { to: "/account", label: t("footer.dashboard") },
        { to: "mailto:contact@rookieradar.eu", label: t("footer.contact") },
      ],
    },
  ];
  return (
    <footer className="mt-16 border-t border-[#1F293D]" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-12 sm:grid-cols-2 sm:px-6 lg:grid-cols-4 lg:px-8">
        {cols.map((col) => (
          <nav key={col.title} aria-label={col.title}>
            <p className="mb-3 border-b border-[#1F293D] pb-2 font-mono2 text-[10px] uppercase tracking-widest text-[#D946EF]">{col.title}</p>
            <ul className="space-y-2">
              {col.links.map((l) => (
                <li key={l.label}>
                  {l.to.startsWith("mailto") ? (
                    <a href={l.to} className="text-xs text-slate-400 transition-colors hover:text-slate-100">{l.label}</a>
                  ) : (
                    <Link
                      to={l.to}
                      {...(l.blank ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      className="text-xs text-slate-400 transition-colors hover:text-slate-100"
                    >
                      {l.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        ))}
        <div>
          <p className="mb-3 border-b border-[#1F293D] pb-2 font-mono2 text-[10px] uppercase tracking-widest text-[#D946EF]">{t("footer.newsletter_title")}</p>
          <p className="mb-3 text-[11px] leading-snug text-slate-500">{t("footer.newsletter_sub")}</p>
          <NewsletterForm />
        </div>
      </div>
      <div className="border-t border-[#1F293D]">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-4 sm:px-6 lg:px-8">
          <span className="font-mono2 text-[10px] text-slate-500">© 2026 Rookie Radar — {t("footer.rights")}</span>
          <div className="flex overflow-hidden rounded border border-[#1F293D]">
            {["en", "fr"].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                data-testid={`footer-lang-${l}`}
                className={`px-2 py-0.5 font-mono2 text-[10px] font-bold uppercase ${lang === l ? "bg-[#00FF87] text-[#0B0E14]" : "text-slate-500"}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
